class ClStudyReasonsCarousel extends HTMLElement {
  connectedCallback() {
    if (this.hasConnected) return;

    this.hasConnected = true;
    this.carousel = this.querySelector('[data-study-reasons-carousel]');
    this.mediaContainer = this.querySelector('.cl-study-reasons__media');
    this.slides = Array.from(this.querySelectorAll('.cl-study-reasons__swiper .swiper-wrapper > [data-study-reasons-slide]'));
    this.mediaSlides = Array.from(this.querySelectorAll('[data-study-reasons-media]'));
    this.controls = this.querySelector('[data-study-reasons-controls]');
    this.dots = this.querySelector('[data-study-reasons-dots]');
    this.handleClick = this.onClick.bind(this);
    this.handleBlockSelect = this.onBlockSelect.bind(this);

    this.addEventListener('click', this.handleClick);
    this.addEventListener('shopify:block:select', this.handleBlockSelect);

    if (!this.carousel || this.slides.length === 0) {
      this.controls?.classList.add('is-hidden');
      return;
    }

    this.mediaSlides.forEach((mediaSlide) => {
      this.mediaContainer?.append(mediaSlide);
    });

    this.renderDots();
    this.controls?.classList.toggle('is-hidden', this.slides.length <= 1);
    this.updateState(0);

    this.waitForSwiper();
  }

  disconnectedCallback() {
    this.hasConnected = false;
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('shopify:block:select', this.handleBlockSelect);

    if (this.swiperWaitTimer) {
      window.clearTimeout(this.swiperWaitTimer);
      this.swiperWaitTimer = null;
    }

    if (this.swiper && !this.swiper.destroyed) {
      this.swiper.destroy(true, true);
    }

    this.swiper = null;
  }

  waitForSwiper(attempt = 0) {
    if (!this.isConnected) return;

    if (typeof window.Swiper === 'function') {
      this.initializeCarousel();
      return;
    }

    if (attempt >= 100) return;

    this.swiperWaitTimer = window.setTimeout(() => {
      this.waitForSwiper(attempt + 1);
    }, 50);
  }

  initializeCarousel() {
    if (this.swiper || !this.carousel) return;

    const autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1;
    const autoplayDelay = Number.parseInt(this.dataset.autoplayDelay, 10) || 5000;
    const loopEnabled = this.slides.length > 1;
    const slidesPerView = (maximum) => Math.min(maximum, Math.max(1, this.slides.length - 1));

    this.swiper = new window.Swiper(this.carousel, {
      slidesPerView: 1,
      spaceBetween: 18,
      speed: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 450,
      allowTouchMove: this.slides.length > 1,
      loop: loopEnabled,
      observer: true,
      observeParents: true,
      watchOverflow: true,
      breakpoints: {
        750: {
          slidesPerView: slidesPerView(2),
        },
        1100: {
          slidesPerView: slidesPerView(3),
        },
      },
      autoplay: autoplayEnabled
        ? {
            delay: autoplayDelay,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }
        : false,
      on: {
        init: (swiper) => this.updateState(swiper.realIndex),
        slideChange: (swiper) => this.updateState(swiper.realIndex),
      },
    });
  }

  onClick(event) {
    if (!this.swiper) return;

    const previousButton = event.target.closest('[data-study-reasons-previous]');
    const nextButton = event.target.closest('[data-study-reasons-next]');
    const dotButton = event.target.closest('[data-study-reasons-go-to]');

    if (previousButton) {
      event.preventDefault();
      this.goToRelativeSlide(-1);
      return;
    }

    if (nextButton) {
      event.preventDefault();
      this.goToRelativeSlide(1);
      return;
    }

    if (dotButton) {
      event.preventDefault();
      const targetIndex = Number.parseInt(dotButton.dataset.studyReasonsGoTo, 10);
      if (Number.isInteger(targetIndex)) this.goToSlide(targetIndex);
    }
  }

  onBlockSelect(event) {
    if (!this.swiper) return;

    const selectedBlock = event.target.closest('[data-study-reasons-block]');
    if (!selectedBlock) return;

    const targetIndex = this.slides.findIndex(
      (slide) => slide.dataset.studyReasonsBlockId === selectedBlock.dataset.studyReasonsBlockId,
    );

    if (targetIndex >= 0) this.goToSlide(targetIndex, 0);
  }

  renderDots() {
    if (!this.dots) return;

    this.dots.replaceChildren();

    this.slides.forEach((slide, index) => {
      const dot = document.createElement('button');
      const label = slide.dataset.studyReasonsLabel || `Motivo ${index + 1}`;

      dot.className = 'cl-study-reasons__dot';
      dot.type = 'button';
      dot.dataset.studyReasonsGoTo = String(index);
      dot.setAttribute('aria-label', `Ir para ${label}`);
      this.dots.append(dot);
    });
  }

  goToRelativeSlide(direction) {
    if (this.slides.length <= 1) return;

    if (direction < 0) {
      this.swiper.slidePrev();
    } else {
      this.swiper.slideNext();
    }
  }

  goToSlide(targetIndex, speed) {
    if (this.swiper.params.loop) {
      this.swiper.slideToLoop(targetIndex, speed);
    } else {
      this.swiper.slideTo(targetIndex, speed);
    }
  }

  updateState(activeIndex) {
    this.querySelectorAll('[data-study-reasons-go-to]').forEach((dot) => {
      const dotIndex = Number.parseInt(dot.dataset.studyReasonsGoTo, 10);
      const isActive = dotIndex === activeIndex;

      dot.classList.toggle('is-active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === activeIndex;

      slide.classList.toggle('is-active', isActive);
      if (isActive) {
        slide.setAttribute('aria-current', 'true');
      } else {
        slide.removeAttribute('aria-current');
      }
    });

    this.mediaSlides.forEach((mediaSlide, mediaIndex) => {
      const isActive = mediaIndex === activeIndex;

      mediaSlide.classList.toggle('is-active', isActive);
      mediaSlide.setAttribute('aria-hidden', String(!isActive));
    });
  }
}

if (!customElements.get('cl-study-reasons-carousel')) {
  customElements.define('cl-study-reasons-carousel', ClStudyReasonsCarousel);
}
